// Thin share-target helper used by the share modal. On native iOS/Android we
// delegate to the @capacitor/share plugin so the OS share sheet appears; on
// the web we mirror the pre-existing ShareModal behavior — prefer the Web
// Share API, fall back to clipboard. User-cancellation of the native sheet is
// swallowed (not an error); other failures rethrow so the caller can fall
// back / surface a message.

import { Share } from '@capacitor/share';
import { isNativePlatform } from './platform.js';

export interface ShareUrlOptions {
  readonly title: string;
  readonly text?: string;
  readonly url: string;
}

interface NavigatorShareTarget {
  readonly url: string;
  readonly title?: string;
  readonly text?: string;
}

interface NavigatorWithShare {
  readonly share?: (data: NavigatorShareTarget) => Promise<void>;
  readonly clipboard?: { writeText?: (text: string) => Promise<void> };
}

function getNav(): NavigatorWithShare | null {
  if (typeof navigator === 'undefined') return null;
  return navigator as unknown as NavigatorWithShare;
}

function isCancellation(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const message = (err as { message?: unknown }).message;
  if (typeof message !== 'string') return false;
  const lower = message.toLowerCase();
  return lower.includes('cancel') || lower.includes('abort') || lower.includes('dismiss');
}

async function shareWeb(options: ShareUrlOptions): Promise<void> {
  const target: NavigatorShareTarget = {
    title: options.title,
    text: options.text ?? options.title,
    url: options.url,
  };
  const nav = getNav();
  if (nav?.share !== undefined) {
    try {
      await nav.share(target);
      return;
    } catch (err) {
      if (isCancellation(err)) return;
      // Fall through to clipboard fallback below.
    }
  }
  const clipboard = nav?.clipboard;
  if (clipboard?.writeText !== undefined) {
    await clipboard.writeText(options.url);
    return;
  }
  throw new Error('share unavailable');
}

export async function shareUrl({ title, text, url }: ShareUrlOptions): Promise<void> {
  if (isNativePlatform()) {
    try {
      await Share.share({ title, text, url });
      return;
    } catch (err) {
      if (isCancellation(err)) return;
      throw err;
    }
  }
  await shareWeb({ title, text, url });
}
